import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getLeccionesEditadasPorEditorial } from "../../services/Api/HistorialUma";
import { getNombreLeccion, ordenarPorFechaDesc } from "./historialLeccionesUtils";
import { UserContext } from "../../services/user";
import { usePagination } from "../../hooks/usePagination";
import alertConfiguration from "../../utils/alertsUtils";
import HistorialLeccionesTable from "./fragments/HistorialLeccionesTable";
import InputBuscar from "../../fragments/Inputs/InputBuscar";
import LoadingScreen from "../../fragments/LoadingScreen";
import ModalHistorialLeccion from "./fragments/ModalHistorialLeccion";
import NimbusModal from "../../fragments/NimbusModal";
import NoDataMessage from "../../fragments/NoDataMessage";
import SubHeaderHistorialLecciones from "./fragments/SubHeaderHistorialLecciones";
import TablePagination from "../../fragments/TablePagination";

const itemsPerPage = 20;

const modalStyles = {
  content: {
    maxWidth: "min(90vw, 700px)",
    width: "100%",
  },
};

const HistorialLeccionesHome = () => {
  const { user, activeRole } = useContext(UserContext);
  const [lecciones, setLecciones] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [leccionSeleccionada, setLeccionSeleccionada] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const idEditorial = user?.editorial?._id;

  const leccionesFiltro = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    if (texto === "") return lecciones;

    return lecciones.filter((leccion) =>
      getNombreLeccion(leccion?.uma).toLowerCase().includes(texto)
    );
  }, [lecciones, busqueda]);

  const { next, prev, jump, currentData, currentPage, maxPage } = usePagination(
    leccionesFiltro,
    itemsPerPage
  );

  useEffect(() => {
    if (activeRole === "") return;
    if (!idEditorial) {
      setIsLoading(false);
      return;
    }
    obtenerLecciones();
  }, [activeRole, idEditorial]);

  const obtenerLecciones = async () => {
    try {
      const respuesta = await getLeccionesEditadasPorEditorial(idEditorial);
      // De la edición más reciente a la más vieja.
      setLecciones(ordenarPorFechaDesc(respuesta, (l) => l?.ultimaEdicion));
    } catch (error) {
      console.error(error);
      toast.error(
        "Ocurrió un error al obtener las lecciones con movimientos.",
        alertConfiguration
      );
    } finally {
      setIsLoading(false);
    }
  };

  const totalMostrado = Math.min(
    currentPage * itemsPerPage,
    leccionesFiltro.length
  );

  return (
    <>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <div className="bg-backgroundColor pb-4 h-[calc(100vh-4.25rem)] flex flex-col">
          <SubHeaderHistorialLecciones />
          <div className="relative flex-1 flex flex-col px-4 sm:px-8 overflow-hidden">
            <div className="w-full md:flex md:flex-wrap md:justify-between space-y-5 md:space-y-0 py-6 items-start">
              <div className="flex flex-wrap items-center">
                <p className="mr-3 font-bold text-nimbus border-y-4 border-cyan-400 rounded-lg px-3 flex items-center justify-center">
                  {totalMostrado} / {leccionesFiltro.length}
                </p>
                <p className="text-black">Lecciones</p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 w-full md:w-auto">
                <InputBuscar
                  ocupaValue
                  value={busqueda}
                  placeholder="Buscar lección..."
                  onChange={(event) => setBusqueda(event.target.value)}
                />
              </div>
            </div>

            <div className="flex-1 overflow-auto px-1">
              {!idEditorial && (
                <NoDataMessage
                  message={
                    "Esta herramienta solo está disponible para usuarios con una editorial asignada."
                  }
                />
              )}
              {idEditorial && leccionesFiltro.length !== 0 && (
                <HistorialLeccionesTable
                  currentData={currentData()}
                  onVerHistorial={setLeccionSeleccionada}
                />
              )}
              {idEditorial && leccionesFiltro.length === 0 && (
                <NoDataMessage
                  message={
                    "No se encontraron lecciones con movimientos registrados."
                  }
                />
              )}
            </div>

            {idEditorial && leccionesFiltro.length !== 0 && (
              <div className="flex-shrink-0">
                <TablePagination
                  next={next}
                  prev={prev}
                  jump={jump}
                  data={currentData()}
                  currentPage={currentPage}
                  maxPage={maxPage}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {leccionSeleccionada && (
        <NimbusModal
          isOpen={true}
          variant="info"
          title={`Historial · ${getNombreLeccion(leccionSeleccionada?.uma)}`}
          styles={modalStyles}
          onRequestClose={() => setLeccionSeleccionada(null)}
        >
          <ModalHistorialLeccion leccion={leccionSeleccionada} />
        </NimbusModal>
      )}
    </>
  );
};

export default HistorialLeccionesHome;
